import React from 'react';
import Layout from '../components/Layout';
import {
  Shield,
  AlertTriangle,
  Clock,
  Lock,
  Heart,
  Users,
  Ban,
  HelpCircle,
  CheckCircle,
} from 'lucide-react';

const ResponsibleGaming: React.FC = () => {
  const warningSigns = [
    {
      icon: Clock,
      title: 'Parier pendant des Périodes Excessives',
      description:
        'Passer beaucoup plus de temps à parier que prévu, en ignorant d\'autres responsabilités.',
    },
    {
      icon: Heart,
      title: 'Symptômes Émotionnels',
      description:
        'Ressentir de l\'anxiété, de l\'irritabilité ou de la dépression quand on ne parie pas.',
    },
    {
      icon: Ban,
      title: 'Tentatives Échouées d\'Arrêter',
      description:
        'Essayer de réduire ou d\'arrêter de parier, mais ne pas réussir à maintenir la décision.',
    },
    {
      icon: AlertTriangle,
      title: 'Mentir sur les Paris',
      description:
        'Cacher à la famille et aux amis l\'ampleur de l\'implication dans les paris.',
    },
    {
      icon: Lock,
      title: 'Problèmes Financiers',
      description:
        'Parier avec de l\'argent nécessaire pour d\'autres dépenses importantes.',
    },
    {
      icon: Users,
      title: 'Isolement Social',
      description:
        'Prioriser les paris sur les relations, le travail ou les activités sociales.',
    },
  ];

  const responsibleMeasures = [
    {
      icon: Shield,
      title: 'Limites de Dépôt',
      description:
        'Établissez des limites quotidiens, hebdomadaires ou mensuels pour vos dépôts.',
    },
    {
      icon: Clock,
      title: 'Limites de Temps',
      description:
        'Configurez des alertes de temps pour contrôler le temps que vous passez à parier.',
    },
    {
      icon: Ban,
      title: 'Auto-exclusion',
      description:
        'Utilisez des outils d\'auto-exclusion temporaire ou permanente si nécessaire.',
    },
    {
      icon: Lock,
      title: 'Vérifications de Réalité',
      description:
        'Activez des rappels réguliers sur le temps et l\'argent que vous avez dépensés.',
    },
  ];

  const helpResources = [
    {
      name: 'Joueurs Info Service',
      description:
        'Service d\'aide et d\'information pour les joueurs en difficulté en France. Numéro: 09 74 75 13 13.',
      link: 'https://www.joueurs-info-service.fr/',
    },
    {
      name: 'ANJ - Autorité Nationale des Jeux',
      description: 'Organisme de régulation des jeux en ligne en France.',
      link: 'https://anj.fr/',
    },
    {
      name: 'Gamblers Anonymous',
      description:
        'Groupes de soutien pour les personnes ayant des problèmes de jeu.',
      link: 'https://www.gamblersanonymous.org',
    },
    {
      name: 'BeGambleAware',
      description:
        'Organisation indépendante qui offre des informations et un soutien sur le jeu responsable.',
      link: 'https://www.begambleaware.org',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Jeu Responsable - Primorixx',
    description:
      'Engagement de Primorixx envers le jeu responsable. Apprenez sur les signes d\'alerte, les mesures de contrôle et les ressources d\'aide.',
    url: 'https://primorixx.com/jeu-responsable',
    mainEntity: {
      '@type': 'Article',
      headline: 'Jeu Responsable - Primorixx',
      author: {
        '@type': 'Organization',
        name: 'Primorixx',
      },
    },
  };

  return (
    <Layout
      title="Jeu Responsable | Primorixx"
      description="Engagement de Primorixx envers le jeu responsable. Apprenez sur les signes d'alerte, les mesures de contrôle et les ressources d'aide."
      jsonLd={jsonLd}
    >
      <div className="min-h-screen bg-dark-bg pt-24 pb-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-dark-surface to-dark-bg">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-dark-accent/20 rounded-2xl mb-6">
                <Shield className="w-8 h-8 text-dark-accent" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 gradient-text">
                Jeu Responsable
              </h1>
              <p className="text-dark-muted text-lg md:text-xl">
                Chez Primorixx, nous prenons le jeu responsable très au sérieux. Le
                divertissement doit toujours être sûr, contrôlé et amusant.
              </p>
            </div>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 gradient-text text-center">
                Notre Engagement
              </h2>
              <div className="bg-dark-card border border-dark-border rounded-xl p-8 mb-12">
                <p className="text-dark-text text-lg leading-relaxed mb-6">
                  Nous croyons que les paris en ligne doivent être une forme de
                  divertissement, pas une source de problèmes. C'est pourquoi,
                  nous promouvons activement les pratiques de jeu responsable et
                  fournissons des informations claires sur la façon de parier en toute sécurité.
                </p>
                <p className="text-dark-text text-lg leading-relaxed">
                  Notre mission est d'aider les parieurs français à faire
                  des choix éclairés et à maintenir le contrôle sur leurs
                  activités de paris. Si vous ou quelqu'un que vous connaissez avez
                  des problèmes avec les paris, des ressources sont disponibles pour vous aider.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Warning Signs */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
                Signes d'Alerte
              </h2>
              <p className="text-dark-muted text-lg max-w-2xl mx-auto">
                Reconnaître les signes de jeu problématique est la première étape
                pour chercher de l'aide.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {warningSigns.map((sign, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <sign.icon className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {sign.title}
                    </h3>
                  </div>
                  <p className="text-dark-muted leading-relaxed">
                    {sign.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Responsible Gaming Measures */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
                Mesures de Jeu Responsable
              </h2>
              <p className="text-dark-muted text-lg max-w-2xl mx-auto">
                Outils et stratégies pour maintenir le contrôle sur vos
                paris.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {responsibleMeasures.map((measure, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-dark-accent/20 rounded-lg flex items-center justify-center">
                      <measure.icon className="w-6 h-6 text-dark-accent" />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {measure.title}
                    </h3>
                  </div>
                  <p className="text-dark-muted leading-relaxed">
                    {measure.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips for Responsible Gaming */}
        <section className="py-20 bg-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 gradient-text text-center">
                Conseils pour Parier de Manière Responsable
              </h2>
              <div className="space-y-4">
                {[
                  'Établissez un budget avant de commencer et ne pariez jamais avec de l\'argent que vous ne pouvez pas perdre.',
                  'Définissez des limites de temps pour vos sessions de paris et respectez-les.',
                  'Ne pariez jamais lorsque vous êtes sous l\'effet de l\'alcool ou d\'autres substances.',
                  'N\'essayez pas de récupérer les pertes en pariant plus. Les pertes font partie du jeu.',
                  'Traitez les paris comme un divertissement, pas comme un moyen de gagner de l\'argent.',
                  'Faites des pauses régulières pendant vos sessions de paris.',
                  'Tenez un registre du temps et de l\'argent dépensés en pariant.',
                  'Si vous ne vous amusez pas, arrêtez de parier.',
                ].map((tip, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 bg-dark-card border border-dark-border rounded-lg p-4"
                  >
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-dark-text leading-relaxed">{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Help Resources */}
        <section className="py-20 bg-dark-bg">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
                Ressources d'Aide
              </h2>
              <p className="text-dark-muted text-lg max-w-2xl mx-auto">
                Si vous avez besoin d'aide, ces organisations sont là pour
                vous soutenir.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {helpResources.map((resource, index) => (
                <div
                  key={index}
                  className="bg-dark-card border border-dark-border rounded-xl p-6 hover:border-dark-accent transition-all duration-300"
                >
                  <div className="flex items-start space-x-3 mb-4">
                    <HelpCircle className="w-6 h-6 text-dark-accent flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {resource.name}
                      </h3>
                      <p className="text-dark-muted mb-4">
                        {resource.description}
                      </p>
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-dark-accent hover:text-dark-accentHover font-medium"
                      >
                        Visitar Site
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-dark-accent/20 to-dark-surface">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 gradient-text">
                Besoin d'Aide?
              </h2>
              <p className="text-dark-muted text-lg mb-8">
                Si vous pensez avoir un problème avec les paris, n'hésitez pas à
                chercher de l'aide. Des ressources sont disponibles et des personnes prêtes
                à vous soutenir.
              </p>
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <p className="text-dark-text text-lg mb-6">
                  Rappelez-vous: les paris doivent être amusants. Si ce n'est plus
                  amusant, il est temps d'arrêter.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <a
                    href="https://www.joueurs-info-service.fr/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-dark-accent hover:bg-dark-accentHover text-white rounded-lg font-semibold transition-colors"
                  >
                    Contacter Joueurs Info Service
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 bg-dark-surface hover:bg-dark-border text-white rounded-lg font-semibold transition-colors border border-dark-border"
                  >
                    Contactez-nous
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ResponsibleGaming;

